import { useMemo, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import type { ApiEnvelope } from '@etherworks/shared-types';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, Clock, Crown, Shield, Sparkles, RefreshCw, ArrowRight, Check, X, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUsage } from '../../contexts/UsageContext';
import {
  TierId,
  DurationId,
  TIERS,
  DURATIONS,
  getPrice,
  getMonthlyDiscountPercent,
  formatTierLabel,
} from '../../constants/subscriptionPlans';
import { useTranslation } from 'react-i18next';
import { formatDateTimeShort, formatDateTime } from '../../utils/dateFormat';

import PageTitle from '../../components/PageTitle';
import { formatCompactNumber } from '../../utils/formatNumber';

interface SubscriptionShortfallCheckoutState {
  tierId: TierId;
  duration: DurationId;
}

interface SubscriptionShortfallOrder {
  codeUrl: string;
  paymentId: string;
  priceUSD: number;
  balanceUSD: number;
  shortfallUSD: number;
  amountCNY: number;
  rate: number;
}

interface PaymentStatusData {
  status: string;
  provider: string;
}

interface AuthMeResponse {
  user?: {
    subscription?: {
      status?: string;
      tier?: string;
      plan?: string;
    };
  };
}

export default function Subscription() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation(['settings', 'common', 'landing']);
  // Balance is denominated in USD; subscription pricing in this flow uses USD.
  const currencySymbol = '$';
  const subscription = user?.subscription;
  const tier = (subscription?.tier || 'free') as TierId | 'free';
  const hasPaidAccess =
    subscription?.status === 'active' &&
    ['plus', 'max', 'ultra'].includes(tier);
  const tierLabel = formatTierLabel(tier);
  const [duration, setDuration] = useState<DurationId>('monthly');
  const [pendingTier, setPendingTier] = useState<TierId | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [shortfallCheckout, setShortfallCheckout] = useState<SubscriptionShortfallCheckoutState | null>(null);

  const activeTier = useMemo(() => {
    if (!user?.subscription || user.subscription.status !== 'active') return null;
    return (user.subscription.tier as TierId) || null;
  }, [user?.subscription]);

  const { usageInfo, refreshUsage } = useUsage();

  useEffect(() => {
    // Poll for usage updates only when this page is active
    const intervalId = setInterval(() => {
      void refreshUsage();
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [refreshUsage]);

  const handleSubscribe = (tierId: TierId) => {
    setPendingTier(tierId);
  };

  const closeConfirm = () => {
    if (confirming) return;
    setPendingTier(null);
  };

  const submitSubscriptionPurchase = useCallback(async (tierId: TierId, planId: DurationId) => {
    setConfirming(true);
    try {
      const res = await axios.post<ApiEnvelope<{ user?: unknown }>>('/api/billing/subscribe-with-balance', {
        tier: tierId,
        plan: planId,
      });
      if (res.data.code === 0 && res.data.data?.user) {
        toast.success(t('subscription.toast_subscribe_success'));
        await refreshUser();
        await refreshUsage();
        return true;
      } else {
        toast.error(res.data.msg || t('subscription.toast_subscribe_fail'));
      }
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === 'INSUFFICIENT_BALANCE') {
        toast.error(t('subscription.confirm_insufficient'));
      } else {
        toast.error(err?.response?.data?.error || t('subscription.toast_subscribe_fail'));
      }
    } finally {
      setConfirming(false);
    }

    return false;
  }, [refreshUsage, refreshUser, t]);

  const submitSubscribe = async () => {
    if (!pendingTier) return;
    const subscribed = await submitSubscriptionPurchase(pendingTier, duration);
    if (subscribed) {
      setPendingTier(null);
    }
  };

  const openShortfallCheckout = () => {
    if (!pendingTier) return;
    setShortfallCheckout({ tierId: pendingTier, duration });
    setPendingTier(null);
  };

  const handleShortfallBalanceReady = useCallback(async (tierId: TierId, planId: DurationId) => {
    const subscribed = await submitSubscriptionPurchase(tierId, planId);
    if (subscribed) {
      setShortfallCheckout(null);
    }
    return subscribed;
  }, [submitSubscriptionPurchase]);

  const handleShortfallPaymentCompleted = useCallback(async (tierId: TierId, planId: DurationId) => {
    try {
      const response = await axios.get<AuthMeResponse>('/api/auth/me');
      const refreshedSubscription = response.data?.user?.subscription;
      await refreshUser();
      await refreshUsage();
      return (
        refreshedSubscription?.status === 'active' &&
        refreshedSubscription?.tier === tierId &&
        refreshedSubscription?.plan === planId
      );
    } catch {
      await refreshUser();
      await refreshUsage();
      return false;
    }
  }, [refreshUsage, refreshUser]);

  const TIER_EXTRA_FEATURES: Record<string, string[]> = {
    plus: ['landing:plans.priority_support'],
    max: ['landing:plans.extended_context', 'landing:plans.tools_calling', 'landing:plans.priority_support'],
    ultra: ['landing:plans.priority', 'landing:plans.max_context', 'landing:plans.tools_calling', 'landing:plans.dedicated_support'],
  };

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden">
      <PageTitle 
        titleKey="subscription.title" 
        namespace="settings" 
        descriptionKey="subscription.subtitle"
      />
      {/* Section Header */}
      <div className="shrink-0">
        <h2 className="text-lg font-semibold text-foreground tracking-tight">{t('subscription.title')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('subscription.subtitle')}
        </p>
      </div>

      {/* Top Row: 2 Stat Cards — left & right */}
      <div className="shrink-0 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: Current Plan */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex flex-col relative overflow-hidden">
          <div className="flex items-center gap-3 mb-3 relative z-10">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${hasPaidAccess ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
              {tier === 'max' ? <Shield className="h-4 w-4" /> : tier === 'ultra' ? <Crown className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('subscription.current_plan')}</p>
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                {tierLabel}
                {hasPaidAccess && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-semibold uppercase tracking-wide">
                    {t('common:active')}
                  </span>
                )}
              </h3>
            </div>
          </div>

          <div className="space-y-0.5 text-xs">
            <div className="flex items-center justify-between py-1.5 border-t border-border/50">
              <span className="text-muted-foreground">{t('subscription.message_limit')}</span>
              <span className="font-medium text-foreground">
                {hasPaidAccess ? `${TIERS.find(p => p.tier === tier)?.usageLimit ?? 0} ${t('subscription.per_window')}` : t('subscription.free_limit')}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-border/50">
              <span className="text-muted-foreground">{t('subscription.token_limit')}</span>
              <span className="font-medium text-foreground">
                {hasPaidAccess ? `${formatCompactNumber(TIERS.find(p => p.tier === tier)?.tokenLimit ?? 0)} ${t('subscription.per_window')}` : t('subscription.free_token_limit')}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-border/50">
              <span className="text-muted-foreground">{t('subscription.billing_cycle')}</span>
              <span className="font-medium text-foreground capitalize">
                {user?.subscription?.plan ? t('landing:plans.duration_' + user.subscription.plan) : t('subscription.free')}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-border/50">
              <span className="text-muted-foreground">{t('subscription.next_payment')}</span>
              <span className="font-medium text-foreground">
                {user?.subscription?.expiresAt ? (
                  formatDateTimeShort(user.subscription.expiresAt)
                ) : (
                  '—'
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Current Usage */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex flex-col">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t('subscription.current_usage')}</p>

          {usageInfo ? (
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-end gap-2 mb-1">
                <span className="text-2xl font-bold text-foreground">{usageInfo.count}</span>
                <span className="text-xs text-muted-foreground mb-1">/ {usageInfo.limit} {t('subscription.used')}</span>
              </div>

              <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    (usageInfo.count / usageInfo.limit) > 0.8 ? 'bg-destructive' :
                    (usageInfo.count / usageInfo.limit) > 0.5 ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, (usageInfo.count / usageInfo.limit) * 100)}%` }}
                />
              </div>

              <div className="flex items-end gap-2 mb-1">
                <span className="text-lg font-bold text-foreground">{formatCompactNumber(usageInfo.tokenCount)}</span>
                <span className="text-xs text-muted-foreground mb-0.5">/ {formatCompactNumber(usageInfo.tokenLimit)} {t('subscription.tokens_used')}</span>
              </div>

              <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    usageInfo.tokenLimit > 0 && (usageInfo.tokenCount / usageInfo.tokenLimit) > 0.8 ? 'bg-destructive' :
                    usageInfo.tokenLimit > 0 && (usageInfo.tokenCount / usageInfo.tokenLimit) > 0.5 ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${usageInfo.tokenLimit > 0 ? Math.min(100, (usageInfo.tokenCount / usageInfo.tokenLimit) * 100) : 0}%` }}
                />
              </div>

              <div className="mt-auto pt-2 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>{t('subscription.resets_at')} {formatDateTime(usageInfo.resetsAt)}</span>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
              {t('subscription.loading_usage')}
            </div>
          )}
        </div>
      </div>

      {/* Plans Section — fills remaining height */}
      <div className="flex-1 min-h-0 flex flex-col bg-card rounded-lg border border-border shadow-xs overflow-hidden">
        {/* Plans Header */}
        <div className="shrink-0 px-5 py-3 border-b border-border bg-muted/30 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t('subscription.available_plans')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t('subscription.plans_subtitle')}</p>
          </div>

          {/* Duration toggle */}
          <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50">
            {DURATIONS.map(d => (
              <button
                key={d.id}
                onClick={() => setDuration(d.id)}
                className={`py-1.5 px-4 rounded-lg text-xs font-medium transition-all duration-200 ${
                  duration === d.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                {t('landing:plans.duration_' + d.id)}
              </button>
            ))}
          </div>
        </div>

        {/* Plan Cards Grid */}
        <div className="flex-1 min-h-0 p-5 overflow-hidden">
          <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-4">
            {TIERS.map(tier => {
              const price = getPrice(tier.tier, duration);
              const discount = getMonthlyDiscountPercent(tier.tier, duration);
              const isActive = activeTier === tier.tier;
              const isPopular = tier.tier === 'max';

              // Determine relationship to current plan
              const tierOrder: Record<string, number> = { plus: 0, max: 1, ultra: 2 };
              const currentRank = activeTier ? tierOrder[activeTier] ?? -1 : -1;
              const thisRank = tierOrder[tier.tier] ?? 0;
              const isDowngrade = !!activeTier && thisRank < currentRank;
              // Allow renewing the same active tier; only disable strict downgrades.
              const canPurchase = !isDowngrade;

              // Determine tier colors
              let tierGradient = '';
              let tierBorder = '';
              let tierRing = '';
              let tierBadge = '';
              let tierCheck = '';
              
              let tierButtonInactive = '';

              if (tier.tier === 'plus') {
                tierGradient = 'bg-card';
                tierBorder = 'border-border hover:border-primary/40';
                tierRing = 'ring-primary/30';
                tierBadge = 'bg-primary text-primary-foreground';
                tierCheck = 'text-primary';
                tierButtonInactive = 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs';
              } else if (tier.tier === 'max') {
                tierGradient = 'bg-card';
                tierBorder = 'border-border hover:border-primary/40';
                tierRing = 'ring-primary/30';
                tierBadge = 'bg-primary text-primary-foreground';
                tierCheck = 'text-primary';
                tierButtonInactive = 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs';
              } else if (tier.tier === 'ultra') {
                tierGradient = 'bg-card';
                tierBorder = 'border-border hover:border-primary/40';
                tierRing = 'ring-primary/30';
                tierBadge = 'bg-primary text-primary-foreground';
                tierCheck = 'text-primary';
                tierButtonInactive = 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs';
              }

              return (
                <div
                  key={tier.tier}
                  className={`relative ${tierGradient} rounded-lg border flex flex-col transition-colors h-full ${
                    isActive
                      ? `border-primary ring-1 ring-primary`
                      : isPopular
                        ? `${tierBorder} ring-1 ${tierRing}`
                        : `${tierBorder}`
                  }`}
                >
                  {isActive && (
                    <div className="absolute -top-2.5 inset-x-0 flex justify-center">
                      <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {t('subscription.current_plan_badge')}
                      </span>
                    </div>
                  )}

                  <div className="p-4 flex-1 flex flex-col">
                    <div className="mb-2">
                      <h3 className="text-sm font-bold text-foreground">{tier.label}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('subscription.messages_per_h', { count: tier.usageLimit, hours: tier.windowHours })}</p>
                    </div>

                    <div className="mb-3 flex items-baseline gap-1">
                      <span className="text-xl font-extrabold text-foreground">{currencySymbol}{price}</span>
                      <span className="text-xs font-medium text-muted-foreground">/{t('landing:plans.duration_' + duration)}</span>
                      {discount > 0 && (
                        <span className="ml-1 inline-flex items-center self-center rounded-full bg-emerald-500/10 text-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold">
                          {t('landing:plans.save_percent', { percent: discount })}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => canPurchase && handleSubscribe(tier.tier)}
                      disabled={!canPurchase || pendingTier === tier.tier}
                      className={`w-full py-1.5 px-4 rounded-xl font-semibold text-xs transition-all duration-200 flex items-center justify-center gap-2 ${
                        isDowngrade
                          ? 'bg-muted/50 text-muted-foreground/60 border border-border cursor-not-allowed opacity-60'
                          : isActive
                            ? tierButtonInactive
                            : tierButtonInactive
                      }`}
                    >
                      {pendingTier === tier.tier && confirming ? (
                        <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t('common:processing')}</>
                      ) : isDowngrade ? (
                        <>{t('subscription.already_on', { tier: formatTierLabel(activeTier) })}</>
                      ) : isActive ? (
                        <>{t('subscription.renew', { tier: tier.label })} <ArrowRight className="h-3.5 w-3.5" /></>
                      ) : (
                        <>{t('subscription.upgrade_to', { tier: tier.label })} <ArrowRight className="h-3.5 w-3.5" /></>
                      )}
                    </button>

                    <div className="mt-3 flex-1">
                      <div className="mb-3 rounded-md border border-border/60 p-2.5">
                        {(() => {
                          const parts = t('landing:plans.tier_desc_' + tier.tier).split(/\s*[:：、,]\s*/).filter(Boolean);
                          const [category, ...models] = parts;
                          return (
                            <>
                              <p className="text-xs font-semibold text-foreground mb-1.5">{category}</p>
                              <ul className="space-y-1">
                                {models.map((m, i) => (
                                  <li key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <span className="h-1 w-1 rounded-full bg-muted-foreground/40 shrink-0" />
                                    {m}
                                  </li>
                                ))}
                              </ul>
                            </>
                          );
                        })()}
                      </div>
                      <p className="text-[10px] font-semibold text-foreground uppercase tracking-wider mb-2">{t('subscription.whats_included')}</p>
                      <ul className="space-y-2.5">
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Check className={`h-3.5 w-3.5 shrink-0 ${tierCheck}`} />
                          <span>{t('landing:plans.messages_window', { count: tier.usageLimit, hours: tier.windowHours })}</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Check className={`h-3.5 w-3.5 shrink-0 ${tierCheck}`} />
                          <span>{t('landing:plans.tokens_window', { tokens: formatCompactNumber(tier.tokenLimit), hours: tier.windowHours })}</span>
                        </li>
                        {(TIER_EXTRA_FEATURES[tier.tier] || []).map(key => (
                          <li key={key} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Check className={`h-3.5 w-3.5 shrink-0 ${tierCheck}`} />
                            <span>{t(key)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {pendingTier && (
        <ConfirmSubscribeModal
          tierId={pendingTier}
          duration={duration}
          balance={user?.balance ?? 0}
          currentExpiry={user?.subscription?.expiresAt}
          confirming={confirming}
          onCancel={closeConfirm}
          onConfirm={submitSubscribe}
          onTopUp={openShortfallCheckout}
          t={t}
        />
      )}

      {shortfallCheckout && (
        <SubscriptionShortfallModal
          tierId={shortfallCheckout.tierId}
          duration={shortfallCheckout.duration}
          onClose={() => setShortfallCheckout(null)}
          onBalanceSufficient={handleShortfallBalanceReady}
          onCompleted={handleShortfallPaymentCompleted}
          t={t}
        />
      )}
    </div>
  );
}

// ── Confirm subscription modal ────────────────────────────────────────────
function ConfirmSubscribeModal({
  tierId, duration, balance, currentExpiry, confirming, onCancel, onConfirm, onTopUp, t,
}: {
  tierId: TierId;
  duration: DurationId;
  balance: number;
  currentExpiry?: string;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onTopUp: () => void;
  t: (key: string, opts?: any) => string;
}) {
  const tierDef = TIERS.find(x => x.tier === tierId);
  const durDef = DURATIONS.find(d => d.id === duration);
  const price = getPrice(tierId, duration);
  const days = durDef?.durationDays ?? 30;

  const now = Date.now();
  const msPerDay = 24 * 60 * 60 * 1000;
  const currentExpiryDate = currentExpiry ? new Date(currentExpiry) : null;
  const baseTime = currentExpiryDate && currentExpiryDate.getTime() > now
    ? currentExpiryDate.getTime()
    : now;
  const newExpiryDate = new Date(baseTime + days * msPerDay);

  const balanceAfter = balance - price;
  const insufficient = balanceAfter < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full max-w-md mx-4 rounded-lg border border-border bg-card p-6 shadow-lg animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onCancel}
          disabled={confirming}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Wallet className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">{t('subscription.confirm_modal_title')}</h2>
        </div>

        <div className="space-y-2.5 text-sm">
          <Row label={t('subscription.confirm_plan')} value={tierDef?.label || tierId} />
          <Row label={t('subscription.confirm_duration')} value={t('landing:plans.duration_' + duration)} />
          <Row
            label={t('subscription.confirm_price')}
            value={<span className="font-semibold tabular-nums">${price.toFixed(2)} USD</span>}
          />
          <div className="border-t border-border/50 my-2" />
          <Row
            label={t('subscription.confirm_current_expiry')}
            value={currentExpiryDate ? formatDateTimeShort(currentExpiryDate) : t('subscription.none')}
          />
          <Row
            label={t('subscription.confirm_new_expiry')}
            value={<span className="font-semibold">{formatDateTimeShort(newExpiryDate)}</span>}
          />
          <div className="border-t border-border/50 my-2" />
          <Row
            label={t('subscription.confirm_current_balance')}
            value={<span className="tabular-nums">${balance.toFixed(2)}</span>}
          />
          <Row
            label={t('subscription.confirm_balance_after')}
            value={
              <span className={`tabular-nums font-semibold ${insufficient ? 'text-destructive' : 'text-foreground'}`}>
                ${balanceAfter.toFixed(2)}
              </span>
            }
          />
        </div>

        {insufficient && (
          <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            {t('subscription.confirm_insufficient')}
          </div>
        )}

        <div className="flex gap-2 pt-5">
          <button
            onClick={onCancel}
            disabled={confirming}
            className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t('subscription.confirm_cancel')}
          </button>
          {insufficient ? (
            <button
              onClick={onTopUp}
              className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('subscription.confirm_topup')}
            </button>
          ) : (
            <button
              onClick={onConfirm}
              disabled={confirming}
              className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {confirming ? (
                <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t('subscription.confirm_processing')}</>
              ) : (
                t('subscription.confirm_submit')
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function SubscriptionShortfallModal({
  tierId,
  duration,
  onClose,
  onBalanceSufficient,
  onCompleted,
  t,
}: {
  tierId: TierId;
  duration: DurationId;
  onClose: () => void;
  onBalanceSufficient: (tierId: TierId, duration: DurationId) => Promise<boolean>;
  onCompleted: (tierId: TierId, duration: DurationId) => Promise<boolean>;
  t: (key: string, opts?: any) => string;
}) {
  const [creating, setCreating] = useState(true);
  const [order, setOrder] = useState<SubscriptionShortfallOrder | null>(null);
  const [paid, setPaid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const createOrder = async () => {
      setCreating(true);
      setErrorMessage(null);

      try {
        const { data } = await axios.post<ApiEnvelope<SubscriptionShortfallOrder>>(
          '/api/billing/subscriptions/wechat-pay/create-order',
          { tier: tierId, plan: duration },
        );

        if (cancelled) return;
        if (data.code === 0 && data.data?.codeUrl && data.data?.paymentId) {
          setOrder(data.data);
          return;
        }

        setErrorMessage(data.msg || t('subscription.shortfall_create_failed'));
      } catch (err: any) {
        if (cancelled) return;

        const code = err?.response?.data?.error;
        if (code === 'BALANCE_ALREADY_SUFFICIENT') {
          const subscribed = await onBalanceSufficient(tierId, duration);
          if (!cancelled && !subscribed) {
            setErrorMessage(t('subscription.shortfall_create_failed'));
          }
          return;
        }

        setErrorMessage(err?.response?.data?.error || t('subscription.shortfall_create_failed'));
      } finally {
        if (!cancelled) {
          setCreating(false);
        }
      }
    };

    void createOrder();

    return () => {
      cancelled = true;
    };
  }, [duration, onBalanceSufficient, t, tierId]);

  useEffect(() => {
    if (!order || paid) return;

    const id = setInterval(async () => {
      try {
        const { data } = await axios.get<ApiEnvelope<PaymentStatusData>>(`/api/billing/payment-status/${order.paymentId}`);
        if (data.code !== 0 || !data.data?.status) {
          return;
        }

        if (data.data.status === 'completed') {
          clearInterval(id);
          const activated = await onCompleted(tierId, duration);
          if (activated) {
            toast.success(t('subscription.toast_subscribe_success'));
            setPaid(true);
            return;
          }

          setOrder(null);
          setErrorMessage(t('subscription.shortfall_activation_failed'));
          return;
        }

        if (data.data.status === 'failed' || data.data.status === 'cancelled') {
          clearInterval(id);
          setOrder(null);
          setErrorMessage(t('subscription.shortfall_failed'));
        }
      } catch {
        // Ignore transient poll failures.
      }
    }, 2000);

    return () => clearInterval(id);
  }, [duration, onCompleted, order, paid, t, tierId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 rounded-lg border border-border bg-card p-6 shadow-lg animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Wallet className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">{t('subscription.shortfall_title')}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('subscription.shortfall_subtitle')}</p>

        {creating ? (
          <div className="flex flex-col items-center py-8 text-center">
            <RefreshCw className="h-10 w-10 animate-spin text-primary mb-3" />
            <p className="text-sm font-medium text-foreground">{t('subscription.shortfall_creating')}</p>
          </div>
        ) : errorMessage ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {errorMessage}
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('subscription.topup_close')}
            </button>
          </div>
        ) : paid && order ? (
          <div className="flex flex-col items-center py-6">
            <CheckCircle className="h-12 w-12 text-emerald-500 mb-3" />
            <p className="text-base font-semibold text-foreground mb-1">{t('subscription.shortfall_success')}</p>
            <p className="text-xs text-muted-foreground text-center">{t('subscription.shortfall_success_hint')}</p>
            <button
              onClick={onClose}
              className="mt-4 rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('subscription.topup_close')}
            </button>
          </div>
        ) : order ? (
          <div className="flex flex-col items-center">
            <div className="w-full space-y-2.5 text-sm mb-4">
              <Row label={t('subscription.confirm_plan')} value={formatTierLabel(tierId)} />
              <Row label={t('subscription.confirm_duration')} value={t('landing:plans.duration_' + duration)} />
              <Row
                label={t('subscription.shortfall_total_price')}
                value={<span className="font-semibold tabular-nums">${order.priceUSD.toFixed(2)} USD</span>}
              />
              <Row
                label={t('subscription.shortfall_balance_applied')}
                value={<span className="tabular-nums">-${order.balanceUSD.toFixed(2)} USD</span>}
              />
              <Row
                label={t('subscription.shortfall_due_usd')}
                value={<span className="font-semibold tabular-nums">${order.shortfallUSD.toFixed(2)} USD</span>}
              />
              <Row
                label={t('subscription.shortfall_due_cny')}
                value={<span className="font-semibold tabular-nums">¥{order.amountCNY.toFixed(2)}</span>}
              />
            </div>

            <p className="text-xs text-muted-foreground mb-3">{t('subscription.shortfall_scan')}</p>
            <div className="bg-white p-3 rounded-xl border border-border">
              <QRCodeSVG value={order.codeUrl} size={200} />
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              <span>{t('subscription.shortfall_waiting')}</span>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">{t('subscription.shortfall_rate_hint', { rate: order.rate.toFixed(4) })}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
